/**!
 * Created by Leonardo Cheslacki on 15/05/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import HelpersController from './helpers/HelpersController';

import VARIABLES from './../../../variables.json';

import mainConfig from './../../../config/main.config';

class AnimateController extends HelpersController {
    constructor() {
        super();

        this.state = {};

        this.$shapeArea = null;

        AnimateController._this = this;
    };

    $functionsAnimate = {

        /**
         * REVIEW
         * @param track
         * @returns {(function(): void)|*}
         */
        handlePlayAudio: (track) => function () {
            const _this = AnimateController._this;
            _this.readAudioSay(track, true);
        },

        /**
         *
         * @param event
         * @param callback
         */
        handleEndAudio: function (event, {callback}) {
            const _this = AnimateController._this;
            _this.pauseAudio(callback);
        },

        /**
         *
         * @param event
         * @param _this_class
         * @param reference
         */
        handleLoadedMetadataVideoAnimate: function (event, {_this_class, reference}) {
            const {video} = mainConfig;
            const _this = AnimateController._this;
            const {element} = _this_class.state;

            if (_this.isElement(element, 'video')) {
                element.currentTime = video.thumbnail_time;

                _this.removeListener('loadedmetadata', reference);
            }
        },

        /**
         *
         * @param event
         * @param _this_class
         * @param reference
         * @param width
         * @param height
         * @param callback
         */
        handleLoadedDataVideoAnimate: function (event, {_this_class, reference, width, height, callback}) {
            const _this = AnimateController._this;
            const {element} = _this_class.state;

            if (_this.isElement(element, 'video')) {
                const
                    buffer = (new createjs.VideoBuffer(element)),
                    bitmap = (new createjs.Bitmap(buffer));

                bitmap.scaleX = _this.calcSize(buffer._video.videoWidth, width, bitmap.scaleX);
                bitmap.scaleY = _this.calcSize(buffer._video.videoHeight, height, bitmap.scaleY);

                _this.removeListener('loadeddata', reference);

                (typeof (callback) === 'function') && callback({duration: element.duration, thumbnail: bitmap});
            }
        }
    };

    /**
     *
     * @param occurrence
     * @returns {*}
     */
    get = (occurrence = null) => {
        if (!this.isEmpty(occurrence)) {
            return AllTap.getState(`_this_${occurrence}`);
        } else {
            const {current_name} = this.props;

            return AllTap.getState(`_this_${current_name}`);
        }
    };

    /**
     *
     * @param occurrence
     * @returns {*}
     */
    getComposition = (occurrence) => {
        if (!this.isEmpty(occurrence)) {
            return AllTap.getState(`composition._this_${occurrence}`);
        } else {
            const {current_name} = this.props;

            return AllTap.getState(`composition._this_${current_name}`);
        }
    };

    /**
     *
     * @returns {string}
     */
    occurrence = () => {
        const {currentFrame} = this.get();

        return this.padWithLeadingZeros(currentFrame, 2);
    };

    /**
     *
     * @param occurrence
     * @param label
     * @returns {*|false|string}
     */
    getFrame = (occurrence = null, label = false) => {
        if (this.isObjectIf(occurrence)) {
            let current;
            if (label) {
                const {currentLabel} = occurrence;
                current = this.toNumber(currentLabel);
            } else {
                const {currentFrame} = occurrence;
                current = currentFrame;
            }

            return occurrence[`occ_frame_${this.padWithLeadingZeros(current, 2)}`];
        } else {
            return this.getObject(this.get(), `occ_frame_${this.occurrence()}`);
        }
    };

    /**
     *
     * @param occ
     * @returns {createjs.Stage|[{answers: number[][], frame: number},{answers: number[][], frame: number},{answers: number[][], frame: number},{answers: number[][], frame: number},{answers: number[][], frame: number}]|number|*|createjs.Stage|boolean}
     */
    getStage = (occ) => {
        return ((this.inObject(occ, 'stage') && (occ.stage instanceof createjs.Stage)) ? occ.stage : (this.inObject(window, 'stage') && (stage instanceof createjs.Stage)) ? stage : false);
    };

    /**
     *
     * @param _this_current
     * @param frame
     */
    gotoAndStopFrame = (_this_current, frame) => {
        _this_current.gotoAndStop(`frame${this.padWithLeadingZeros(frame, 2)}`);
    };

    /**
     *
     * @param occurrence
     * @param state
     * @param callback
     */
    setStateOccurrence = (occurrence, state, callback) => {
        !this.inObject(occurrence, 'state') && (occurrence.state = {});
        occurrence.state = {...occurrence.state, ...state};
        (typeof (callback) === 'function') && callback(true);
    };

    /**
     *
     * @param occurrence
     * @param field
     * @returns {string|*}
     */
    getStateOccurrence = (occurrence, field) => {
        return this.verifyField(occurrence, `state.${field}`);
    };

    /**
     *
     * @param _this_current
     * @param occurrence
     * @param callback
     */
    effectFadeIn = (_this_current, occurrence, callback = null) => {
        if (!this.getReferenceTicker('_handleEffectFadeIn')) {
            const
                {alpha_min, alpha_plus, alpha_max} = this.$controller,
                plus = (new Big(alpha_plus)),
                max = (new Big(alpha_max));

            let big_alpha_min = (new Big(alpha_min)).toNumber();

            if (this.isArrayIf(occurrence)) {
                for (let key in occurrence) {
                    let occ = _this_current[occurrence[key]];
                    !occ.isVisible() && (occ.visible = true);
                    occ.alpha = big_alpha_min;
                }
            } else {
                let occ = _this_current[occurrence];
                !occ.isVisible() && (occ.visible = true);
                occ.alpha = big_alpha_min;
            }

            const execute = () => {
                let
                    occ = this.isArrayIf(occurrence) ? _this_current[this.firstArray(occurrence)] : _this_current[occurrence],
                    alpha = (new Big(occ.alpha)).plus(plus).round(1, Big.roundUp);

                if (this.isArrayIf(occurrence)) {
                    for (let key in occurrence) {
                        _this_current[occurrence[key]].alpha = alpha.toNumber();
                    }
                } else {
                    _this_current[occurrence].alpha = alpha.toNumber();
                }

                if (alpha.gte(max)) {
                    this.destroyTicker('_handleEffectFadeIn', true);
                    (typeof (callback) === 'function') && callback(true);
                }
            };

            //createjs.Ticker.timingMode = createjs.Ticker.RAF;

            let ticker = createjs.Ticker.on('tick', execute);

            this.setReferenceTicker('_handleEffectFadeIn', ticker);
        }
    };

    /**
     *
     * @param _this_current
     * @param occurrence
     * @param callback
     */
    effectFadeOut = (_this_current, occurrence, callback = null) => {
        if (!this.getReferenceTicker('_handleEffectFadeOut')) {
            const
                {alpha_min, alpha_plus, alpha_max} = this.$controller,
                plus = (new Big(alpha_plus)),
                min = (new Big(alpha_min));

            if (this.isArrayIf(occurrence)) {
                for (let key in occurrence) {
                    let occ = _this_current[occurrence[key]];
                    occ.isVisible() && (_this_current[occurrence[key]].alpha = (new Big(alpha_max)).toNumber());
                }
            } else {
                let occ = _this_current[occurrence];
                occ.isVisible() && (_this_current[occurrence].alpha = (new Big(alpha_max)).toNumber());
            }

            const execute = () => {
                let
                    occ = this.isArrayIf(occurrence) ? _this_current[this.firstArray(occurrence)] : _this_current[occurrence],
                    alpha = (new Big(occ.alpha)).minus(plus).round(1, Big.roundUp);

                if (this.isArrayIf(occurrence)) {
                    for (let key in occurrence) {
                        _this_current[occurrence[key]].alpha = alpha.toNumber();
                    }
                } else {
                    _this_current[occurrence].alpha = alpha.toNumber();
                }

                if (alpha.lte(min)) {
                    if (this.isArrayIf(occurrence)) {
                        for (let key in occurrence) {
                            let occ = _this_current[occurrence[key]];
                            occ.isVisible() && (occ.visible = false);
                        }
                    } else {
                        let occ = _this_current[occurrence];
                        occ.isVisible() && (occ.visible = false);
                    }

                    this.destroyTicker('_handleEffectFadeOut', true);

                    (typeof (callback) === 'function') && callback(true);
                }
            };

            //createjs.Ticker.timingMode = createjs.Ticker.RAF;

            let ticker = createjs.Ticker.on('tick', execute);

            this.setReferenceTicker('_handleEffectFadeOut', ticker);
        }
    };

    /**
     *
     * @param uri
     * @param callback
     */
    readAudio = (uri, callback) => {
        const {audio} = mainConfig;
        const {enabled, confirm} = this.state;

        if (!enabled && confirm) {
            this.setState({...this.state, element: document.createElement('audio'), enabled: true}, ({element}) => {
                this.removeListener('ended', 'ended-audio', () => {
                    this.addListener(element, 'ended', 'ended-audio', this.$functionsAnimate.handleEndAudio, false, {
                        callback: callback
                    });
                });

                element.loop = audio.loop;
                element.src = uri;
                element.play();
            });
        }
    };

    /**
     *
     * @param callback
     */
    pauseAudio = (callback) => {
        const _this = AnimateController._this;
        this.setState({...this.state, enabled: false}, ({element}) => {
            _this.isElement(element, 'audio') && element.pause();
            (typeof (callback) === 'function') && callback('ended');
        });
    };

    checkAudio = () => {
        return this.isElement(this.getState('element'), 'audio');
    };

    /**
     *
     * @param track
     * @param animate
     * @param callback
     */
    readAudioSay = (track, animate = false, callback) => {
        const formatted_say_uri = this.verifyField(track, 'formatted_say_uri');

        if (formatted_say_uri) {
            if (animate) {
                const occ_icon_audio = this.verifyField(this.getFrame(), 'occ_title.occ_icon_audio') || this.verifyField(this.getFrame(), `occ_title.occ_icon_audio_${this.occurrence()}`);
                if (occ_icon_audio) {
                    occ_icon_audio.gotoAndPlay('start');
                    this.readAudio(formatted_say_uri, (response) => {
                        occ_icon_audio.gotoAndStop('start');
                        (typeof (callback) === 'function') && callback(response);
                    });
                } else {
                    this.readAudio(formatted_say_uri, callback);
                }
            } else {
                this.readAudio(formatted_say_uri, callback);
            }
        } else {
            (typeof (callback) === 'function') && callback('no-say');
        }
    };

    openVideoAnimate = () => {
        const {video} = mainConfig;
        const
            {element} = this.state,
            type_timeupdate = 'timeupdate',
            reference_timeupdate = 'timeupdate-video',
            type_pause = 'pause',
            reference_pause = 'pause-video',
            type_ended = 'ended',
            reference_ended = 'ended-video';

        if (this.isElement(element, 'video')) {

            let listener = this.getListener(reference_pause);

            if (!this.isEmpty(listener)) {
                element.pause();

                const {data: {status}} = listener;

                if (Boolean(status.pause)) {
                    status.pause = false;
                    element.play();
                }
            } else {
                element.currentTime = video.current_time;
                element.play();
            }

            this.removeListener(type_timeupdate, reference_timeupdate, () => {
                this.addListener(element, type_timeupdate, reference_timeupdate, this.$functions.handleTimeUpdateVideo, false, {
                    element: element
                });
            });

            this.removeListener(type_pause, reference_pause, () => {
                this.addListener(element, type_pause, reference_pause, this.$functions.handlePauseVideo, false, {
                    element: element,
                    status: {pause: false}
                });
            });

            this.removeListener(type_ended, reference_ended, () => {
                this.addListener(element, type_ended, reference_ended, this.$functions.handleEndVideo, false, {
                    callback: () => {
                        element.currentTime = 0;
                    }
                });
            });
        }
    };

    /**
     * Abstract, use timeupdate.
     * @param uri
     * @param occurrence
     * @param width
     * @param height
     * @param callback
     */
    getVideoMetadataAnimate = (uri, occurrence, width, height, callback) => {
        const
            type_loadedmetadata = 'loadedmetadata',
            reference_loadedmetadata = `loadedmetadata-video-${occurrence}`,
            type_loadeddata = 'loadeddata',
            reference_loadeddata = `loadeddata-video-${occurrence}`;

        this.setState({...this.state, element: document.createElement('video')}, ({element}) => {
            this.removeListener(type_loadedmetadata, reference_loadedmetadata, () => {
                this.addListener(element, type_loadedmetadata, reference_loadedmetadata, this.$functionsAnimate.handleLoadedMetadataVideoAnimate, false, {
                    _this_class: this,
                    reference: reference_loadedmetadata
                });
            });

            this.removeListener(type_loadeddata, reference_loadeddata, () => {
                this.addListener(element, type_loadeddata, reference_loadeddata, this.$functionsAnimate.handleLoadedDataVideoAnimate, false, {
                    _this_class: this,
                    reference: reference_loadeddata,
                    width: width,
                    height: height,
                    callback: callback
                });
            });

            element.src = uri;
        });
    };

    /**
     *
     * @param occ
     * @param x
     * @param y
     */
    setCoordinateTransform = (occ, x, y = null) => {
        x = this.toIntegerOrFloat(x);

        if (!this.isEmpty(y)) {
            y = this.toIntegerOrFloat(y);
        } else {
            y = occ.y;
        }

        (this.isIntegerOrFloat(x) && this.isIntegerOrFloat(y)) && occ.setTransform(x, y)
    };

    /**
     *
     * @param reference
     * @param force
     */
    destroyTicker = (reference, force = false) => {
        const ticker = this.getReferenceTicker(reference);

        if (ticker) {
            createjs.Ticker.off('tick', ticker);
            delete AllTap[`${reference}`];
        } else if (force) {
            Boolean(createjs.Ticker.getTicks()) && createjs.Ticker.reset();
        }
    };

    destroyAllTicker = () => {
        for (let property of VARIABLES.ticker) {
            this.destroyTicker(property);
        }
    }

    /**
     *
     * @param reference
     * @returns {string|*}
     */
    getReferenceTicker = (reference) => {
        return this.inFunction(AllTap, reference) && AllTap[`${reference}`];
    };

    /**
     *
     * @param reference
     * @param ticker
     */
    setReferenceTicker = (reference, ticker) => {
        AllTap[`${reference}`] = ticker;
    };

    /**
     *
     * @param occ
     * @param listener
     * @param data
     * @param name
     * @param type
     */
    setHandleMouseDown = (occ, listener, data = null, name = null, type = 'mousedown') => {
        if (this.isObject(occ)) {
            const
                {current_name} = this.props,
                handle_name = this.toHandleName(name || occ.name, current_name);

            this[handle_name] && occ.off(type, this[handle_name]);
            this[handle_name] = this.isEmpty(data) ? occ.on(type, listener) : occ.on(type, listener, false, false, data);
        }
    };

    /**
     *
     * @param stage
     * @param x
     * @param y
     */
    testCoordinate = (stage, x, y) => {
        let graphics = (new createjs.Graphics());
        graphics.setStrokeStyle(1);
        graphics.beginStroke(createjs.Graphics.getRGB(0, 0, 0));
        graphics.beginFill(createjs.Graphics.getRGB(255, 0, 0));
        graphics.drawCircle(0, 0, 10);

        x = (this.inObject(x, 'constructor') ? x.toString() : x);
        y = (this.inObject(y, 'constructor') ? y.toString() : y);

        let shape = new createjs.Shape(graphics);
        shape.x = x;
        shape.y = y;

        console.log(x, y);
        console.log(shape);

        stage.addChild(shape);
    };

    /**
     *
     * @param stage
     * @param width
     * @param height
     * @param x
     * @param y
     */
    testArea = (stage, width, height, x, y) => {
        this.$shapeArea && stage.removeChild(this.$shapeArea);

        let graphics = (new createjs.Graphics());
        graphics.setStrokeStyle(1);
        graphics.beginStroke(createjs.Graphics.getRGB(0, 0, 0));
        graphics.beginFill(createjs.Graphics.getRGB(255, 0, 0));
        graphics.drawRect(0, 0, width, height);

        x = (this.inObject(x, 'constructor') ? x.toString() : x);
        y = (this.inObject(y, 'constructor') ? y.toString() : y);

        this.$shapeArea = new createjs.Shape(graphics);
        this.$shapeArea.x = x;
        this.$shapeArea.y = y;
        this.$shapeArea.alpha = 0.5;

        console.log(width, height, x, y);
        console.log(this.$shapeArea);

        stage.addChild(this.$shapeArea);
    };

    /**
     *
     * @param occ
     * @param count
     * @param thousands
     * @param callback
     */
    odometer = (occ, count, thousands = false, callback) => {
        let
            object = {val: 0, loop: false, override: true},
            duration = 2000;

        occ.text = '00';

        let handleTick = (event) => {
            let
                value = Math.round(object.val),
                string = '';

            if (value < count) {
                string = this.padWithLeadingZeros(value, 2);
            } else {
                string = count;
            }

            occ.text = thousands ? this.formatThousands(string) : string;

            let stage = this.getStage(occ);

            stage && stage.update(event);
        }

        this.destroyTicker('_handleOdometer');

        createjs.Ticker.timingMode = createjs.Ticker.RAF_SYNCHED;

        let ticker = createjs.Ticker.on('tick', handleTick);

        this.setReferenceTicker('_handleOdometer', ticker);

        createjs.Tween.get(object).to({val: count}, duration, createjs.Ease.cubicOut).call(() => {
            occ.text = thousands ? this.formatThousands(count) : count;
            this.destroyTicker('_handleOdometer');
            (typeof (callback) === 'function') && callback(true);
        });
    };
}

/**
 *
 * @type {{}}
 * @private
 */
AnimateController._this = {};

export default AnimateController;
